import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import StatisticsPage, { drilldownPath } from '../../components/statistics/StatisticsPage';
import DataTable, { Column } from '../../components/statistics/DataTable';
import { BarList, Heatmap } from '../../components/statistics/charts';
import { useStatisticsData, useStatisticsFilters } from '../../components/statistics/useStatistics';
import { getMarketplace } from '../../services/statisticsService';
import { WEEKDAYS, WINDOWS, formatCount, formatDecimal, formatShare } from '../../utils/period';

interface RouteRow {
  origin: string;
  destination: string;
  rides: number;
  seatsOffered: number;
  requests: number;
  seatsFilled: number;
  fillRate: number | null;
  unmetCaptures: number;
  unmetRiders: number;
  averagePrice: number | null;
}

const CELL_MEASURES = [
  { key: 'rides', label: 'Rides' },
  { key: 'seatsOffered', label: 'Seats offered' },
  { key: 'requests', label: 'Seat requests' },
  { key: 'seatsFilled', label: 'Seats filled' },
];

/** PRD §5.2 — where to recruit drivers, and for which part of the week. */
const Marketplace = () => {
  const [filters, setFilters] = useStatisticsFilters();
  const [search, setSearch] = useSearchParams();
  const origin = search.get('origin');
  const destination = search.get('destination');
  const route = origin && destination ? { origin, destination } : null;
  const [measure, setMeasure] = useState('rides');

  const { data, loading, error, reload } = useStatisticsData(
    (f) => getMarketplace(f, route),
    filters,
    `${origin}|${destination}`
  );

  const selectRoute = (next: { origin: string; destination: string } | null) => {
    const params = new URLSearchParams(search);
    if (next) {
      params.set('origin', next.origin);
      params.set('destination', next.destination);
    } else {
      params.delete('origin');
      params.delete('destination');
    }
    setSearch(params, { replace: true });
  };

  const routes: RouteRow[] = data?.routes || [];
  const needy = routes.filter((r) => r.unmetRiders > 0 || r.requests > r.seatsFilled).slice(0, 3);
  const cells: any[] = data?.byTime?.cells || [];
  const cell = (weekday: string, window: string) => cells.find((c) => String(c.weekday) === weekday && c.window === window);
  const isSelected = (r: RouteRow) => route?.origin === r.origin && route?.destination === r.destination;

  const columns: Column<RouteRow>[] = [
    {
      key: 'route',
      label: 'Route',
      value: (r) => `${r.origin} → ${r.destination}`,
      render: (r) => (
        <button
          type="button"
          className="stats-link-button"
          onClick={() => selectRoute(isSelected(r) ? null : r)}
          title="Show this route by day and time"
        >
          {r.origin} → {r.destination}
        </button>
      ),
    },
    { key: 'unmetRiders', label: 'Riders waiting', numeric: true },
    { key: 'rides', label: 'Rides', numeric: true },
    { key: 'seatsOffered', label: 'Seats offered', numeric: true },
    { key: 'requests', label: 'Seat requests', numeric: true },
    { key: 'seatsFilled', label: 'Seats filled', numeric: true },
    { key: 'fillRate', label: 'Fill rate', numeric: true, render: (r) => formatShare(r.fillRate) },
    { key: 'averagePrice', label: 'Avg price (₪)', numeric: true, render: (r) => formatDecimal(r.averagePrice) },
  ];

  const unmetByWindow = data?.byTime?.unmetByWindow || {};
  const unmetByWeekday = data?.byTime?.unmetByWeekday || {};

  return (
    <StatisticsPage
      title="Marketplace"
      subtitle="Supply and demand by route. Riders waiting = members watching a route right now with a Ride Capture that has never matched."
      filters={filters}
      onFiltersChange={setFilters}
      period={data?.period}
      loading={loading}
      error={error}
      onRetry={reload}
      showComparison={false}
    >
      {needy.length > 0 && (
        <div className="stats-callout">
          <strong>Most in need of drivers:</strong>{' '}
          {needy.map((r, i) => (
            <span key={`${r.origin}-${r.destination}`}>
              {i > 0 && ' · '}
              {r.origin} → {r.destination} ({formatCount(r.unmetRiders)} waiting, {formatCount(r.requests - r.seatsFilled)} requests unfilled)
            </span>
          ))}
        </div>
      )}

      <section className="stats-section">
        <h2>Routes</h2>
        <p className="stats-section-intro">
          Rides departing in the period (cancelled rides left out). Click a route to see it by day and time below.{' '}
          <Link className="stats-link" to={drilldownPath('rides', filters)}>
            See every ride
          </Link>
        </p>
        <DataTable
          columns={columns}
          rows={routes}
          exportName={`zippel-routes-${data?.period?.from}-to-${data?.period?.to}`}
          rowKey={(r) => `${r.origin}|${r.destination}`}
          empty="No rides or waiting riders in this period."
        />
      </section>

      <section className="stats-section">
        <h2>By day and time{route ? `: ${route.origin} → ${route.destination}` : ', all routes'}</h2>
        <p className="stats-section-intro">
          A route can look healthy in a daily total and still have nothing in the evening.
          {route && (
            <>
              {' '}
              <button type="button" className="stats-link-button" onClick={() => selectRoute(null)}>
                Show all routes
              </button>
            </>
          )}
        </p>
        <label className="stats-control" style={{ marginBottom: 12, maxWidth: 220 }}>
          <span>Show</span>
          <select value={measure} onChange={(e) => setMeasure(e.target.value)}>
            {CELL_MEASURES.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
        <Heatmap
          rows={WEEKDAYS.map((label, i) => ({ key: String(i), label }))}
          columns={WINDOWS}
          value={(row, column) => cell(row, column)?.[measure] || 0}
          describe={(row, column) => {
            const c = cell(row, column);
            return c
              ? `${WEEKDAYS[Number(row)]} ${column}: ${c.rides} rides, ${c.seatsOffered} seats, ${c.requests} requests, ${c.seatsFilled} filled`
              : '';
          }}
        />
      </section>

      <div className="stats-columns">
        <section className="stats-section">
          <h2>Riders waiting, by time of day</h2>
          <p className="stats-section-intro">A Capture watching several windows counts once in each.</p>
          <BarList
            items={[...WINDOWS, { key: 'any', label: 'Any time' }].map((w) => ({
              label: w.label,
              value: unmetByWindow[w.key] || 0,
            }))}
          />
        </section>
        <section className="stats-section">
          <h2>Riders waiting, by day</h2>
          <p className="stats-section-intro">A Capture watching several days counts once on each.</p>
          <BarList
            items={[...WEEKDAYS.map((label, i) => ({ key: String(i), label })), { key: 'any', label: 'Any day' }].map((d) => ({
              label: d.label,
              value: unmetByWeekday[d.key] || 0,
            }))}
          />
        </section>
      </div>
    </StatisticsPage>
  );
};

export default Marketplace;
